// Setup › Price book › Pictures › To approve (BL-122): the words for the new pictures waiting for the owner
// or a manager, what an answer did, and the signal that tells the Setup rail's count to look again.

/** One picture waiting (pricebook.art.toApprove's row). */
export interface ToApprove {
  artId: string;
  subjectId: string;
  words: string;
  look: 'drawing' | 'label';
  items: { id: string; name: string }[];
  sharedBy: number;
  light: { url: string | null; thumbUrl: string | null };
  dark: { url: string | null; thumbUrl: string | null } | null;
  madeAt: string;
  fromLibrary: boolean;
  request: string | null;
}

/** pricebook.art.approve's answer. */
export interface Approved {
  approved: number;
  already: number;
  notWaiting: number;
}

/** Sent on window after an answer: the rail's "waiting" count on Price book reads again. */
export const PICTURES_CHANGED = 'flw:pictures-changed';

const n = (x: number) => x.toLocaleString('en-CA');
const pictures = (x: number) => `${n(x)} picture${x === 1 ? '' : 's'}`;

/** The items that show it: "Ball valve", "Ball valve and Gate valve", "Ball valve, Gate valve and 3 more". */
export function itemsWords(p: Pick<ToApprove, 'items' | 'sharedBy'>): string {
  const names = p.items.map((i) => i.name);
  const more = p.sharedBy - names.length;
  if (!names.length) return 'No item shows it now';
  if (more > 0) return `${names.join(', ')} and ${n(more)} more`;
  if (names.length === 1) return names[0]!;
  return `${names.slice(0, -1).join(', ')} and ${names.at(-1)!}`;
}

/** The line over the grid: how many wait, and that customers see none of them yet. */
export function waitingLine(total: number, shown: number): string {
  if (!total) return 'No new pictures are waiting. Customers and technicians see only approved ones.';
  const more = total > shown ? ` The ${n(shown)} oldest are shown.` : '';
  return `${pictures(total)} waiting. Customers and technicians don’t see a picture until it’s approved.${more}`;
}

/** What an approval did, in words. */
export function approvedWords(r: Approved): string {
  const said: string[] = [];
  if (r.approved)
    said.push(`${pictures(r.approved)} approved: customers see ${r.approved === 1 ? 'it' : 'them'} now.`);
  if (r.already) said.push(`${pictures(r.already)} ${r.already === 1 ? 'was' : 'were'} approved already.`);
  if (r.notWaiting)
    said.push(
      `${pictures(r.notWaiting)} ${r.notWaiting === 1 ? 'isn’t' : 'aren’t'} waiting any more (turned down or replaced).`,
    );
  return said.join(' ') || 'Nothing to approve.';
}

/** What turning one down did: another is on its way, or pictures are off. */
export function turnedDownWords(r: { rejected: boolean; jobId: string | null }): string {
  if (!r.rejected) return 'It was turned down already.';
  return r.jobId
    ? 'Turned down. Another is being made; it counts against this month’s picture budget and waits here too.'
    : 'Turned down. Pictures are off, so no other is made until they’re turned on.';
}
